import { useConsultationHorsLigne } from '../../../horsLigne/index.js';
import { Button, Price, PriceInput, Select } from '@artosera/ui';
import { DEVISES, REGIMES_TVA } from '@artosera/shared';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../../api/errors.js';
import { useModifierOeuvre, type OeuvrePresentee } from '../../../api/oeuvres.js';
import { Field } from '../../Field.js';
import styles from './Tabs.module.css';

/** Borne haute d'un prix saisi : 100 M€, au-delà c'est une faute de frappe. */
export const PRIX_MAX_CENTIMES = 10_000_000_000;

export interface PrixTabProps {
  galerieSlug: string;
  oeuvre: OeuvrePresentee;
  /** Droit d'écriture (`artworks:write`) : sans lui, consultation seule (ARTO-53, § 4). */
  peutEcrire: boolean;
}

/**
 * Prix (3.3, écran 2, 5.1) : prix public, prix artiste si le droit est
 * accordé (le champ n'existe même pas dans la réponse sinon — 12.3), devise,
 * régime de TVA lu directement sur l'œuvre (`tax_profile` n'est pas encore
 * exposé par l'API : écart assumé, voir le rapport final).
 *
 * Sans `artworks:write`, aucun champ ni bouton d'écriture (audit du
 * 23 septembre 2026, § 1 : « Le lecteur voit des champs éditables et
 * Enregistrer ») : une consultation, avec le prix artiste omis plutôt
 * qu'affiché en clair si le droit fin ne le donne pas.
 */
export function PrixTab({ galerieSlug, oeuvre, peutEcrire }: PrixTabProps): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'oeuvres', 'ventes', 'commun']);
  const consultation = useConsultationHorsLigne();
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const modifier = useModifierOeuvre(galerieSlug);

  // `null` = pas de prix (« — ») ; 0 est un vrai prix de zéro. Un champ vidé
  // envoie `null`, jamais 0.
  const [prixPublic, setPrixPublic] = useState<number | null>(oeuvre.prixPublicCentimes);
  const [prixArtiste, setPrixArtiste] = useState<number | null>(oeuvre.prixArtisteCentimes ?? null);
  const [devise, setDevise] = useState(oeuvre.devise);
  const [regimeTva, setRegimeTva] = useState(oeuvre.regimeTva);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState(false);

  const voitPrixArtiste = oeuvre.prixArtisteCentimes !== undefined;
  const publicTropEleve = prixPublic !== null && prixPublic > PRIX_MAX_CENTIMES;
  const artisteTropEleve = prixArtiste !== null && prixArtiste > PRIX_MAX_CENTIMES;
  // Avertissement, pas blocage : un prix artiste supérieur au prix public se
  // vend à perte, mais c'est parfois voulu (soutien, solde).
  const artisteAuDessusDuPublic =
    voitPrixArtiste && prixPublic !== null && prixArtiste !== null && prixArtiste > prixPublic;
  const peutEnregistrer = !publicTropEleve && !artisteTropEleve;

  async function enregistrer(): Promise<void> {
    if (!peutEnregistrer) return;
    setErreur(null);
    setSucces(false);
    try {
      await modifier.mutateAsync({
        id: oeuvre.id,
        corps: {
          prixPublicCentimes: prixPublic,
          ...(voitPrixArtiste ? { prixArtisteCentimes: prixArtiste } : {}),
          devise,
          regimeTva,
        },
      });
      setSucces(true);
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  if (!peutEcrire) {
    return (
      <dl className={styles.donneesGrille}>
        <div>
          <dt>{t('regie:oeuvres.prix.prixPublic')}</dt>
          <dd>
            {oeuvre.prixPublicCentimes !== null ? (
              <Price cents={oeuvre.prixPublicCentimes} lang={langue} currency={oeuvre.devise} />
            ) : (
              t('regie:oeuvres.informations.non')
            )}
          </dd>
        </div>
        {consultation ? (
          <div>
            <dt>{t('regie:oeuvres.prix.prixArtiste')}</dt>
            <dd>{t('regie:horsLigne.nonDisponible')}</dd>
          </div>
        ) : voitPrixArtiste && oeuvre.prixArtisteCentimes !== null ? (
          <div>
            <dt>{t('regie:oeuvres.prix.prixArtiste')}</dt>
            <dd>
              <Price
                cents={oeuvre.prixArtisteCentimes ?? 0}
                lang={langue}
                currency={oeuvre.devise}
              />
            </dd>
          </div>
        ) : null}
        <div>
          <dt>{t('regie:oeuvres.prix.regimeTva')}</dt>
          <dd>{t(`ventes:regimes.${oeuvre.regimeTva}`)}</dd>
        </div>
      </dl>
    );
  }

  return (
    <form
      className={styles.formulaire}
      data-ui="artwork-form"
      onSubmit={(event) => event.preventDefault()}
      noValidate
    >
      <div className={styles.grille2}>
        <Field
          label={t('regie:oeuvres.prix.prixPublic')}
          htmlFor="prix-public"
          error={publicTropEleve ? t('regie:oeuvres.prix.montantTropEleve') : undefined}
        >
          <PriceInput
            id="prix-public"
            optional
            placeholder="—"
            cents={prixPublic}
            onCentsChange={setPrixPublic}
            lang={langue}
            currency={devise}
            aria-invalid={publicTropEleve || undefined}
          />
        </Field>
        <Field label={t('regie:oeuvres.prix.devise')} htmlFor="prix-devise">
          <Select
            id="prix-devise"
            aria-label={t('regie:oeuvres.prix.devise')}
            value={devise}
            onValueChange={(valeur) => setDevise(valeur as typeof devise)}
            options={DEVISES.map((code) => ({ value: code, label: t(`commun:devises.${code}`) }))}
          />
        </Field>
      </div>

      {voitPrixArtiste ? (
        <Field
          label={t('regie:oeuvres.prix.prixArtiste')}
          htmlFor="prix-artiste"
          error={artisteTropEleve ? t('regie:oeuvres.prix.montantTropEleve') : undefined}
        >
          <PriceInput
            id="prix-artiste"
            optional
            placeholder="—"
            cents={prixArtiste}
            onCentsChange={setPrixArtiste}
            lang={langue}
            currency={devise}
            aria-invalid={artisteTropEleve || undefined}
          />
          {artisteAuDessusDuPublic ? (
            <p className={styles.aide} role="status">
              {t('regie:oeuvres.prix.artisteAuDessusDuPublic')}
            </p>
          ) : null}
        </Field>
      ) : (
        <p className={styles.aide}>{t('regie:oeuvres.prix.prixArtisteRefuse')}</p>
      )}

      <Field label={t('regie:oeuvres.prix.regimeTva')} htmlFor="prix-tva">
        <Select
          id="prix-tva"
          aria-label={t('regie:oeuvres.prix.regimeTva')}
          value={regimeTva}
          onValueChange={(valeur) => setRegimeTva(valeur as typeof regimeTva)}
          options={REGIMES_TVA.map((code) => ({ value: code, label: t(`ventes:regimes.${code}`) }))}
        />
      </Field>

      {erreur ? (
        <p className={styles.erreur} role="alert">
          {erreur}
        </p>
      ) : null}
      {succes ? <p className={styles.succes}>{t('oeuvres:messages.modifiee')}</p> : null}

      <Button
        onClick={() => void enregistrer()}
        loading={modifier.isPending}
        disabled={!peutEnregistrer}
      >
        {t('commun:actions.enregistrer')}
      </Button>
    </form>
  );
}
